import React, { useEffect, useState } from "react";
//components
import MapDialog from "../components/maps/modals/MapDialog"
//hooks, context
import useInfoWindow from "./gglMapHooks/useInfoWindow";
import usePoiService from "./pois/usePoiService";
import usePolylineManager from "./pois/usePolylineManager";
import useMarkerManager from "./pois/useMarkerManager";
import usePoiDialogHandlers from "./pois/usePoiDialogHandlers";
import usePoiGroupService from "./pois/usePoiGroupsService";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext";
import { useUser } from "@contexts/UserContext";
/**
 * Used by the MapComponent to display poi-related items on the map. It pulls together functionality from more specialized poi hooks.
 * 
 * @param {React.RefObject<HTMLDivElement>} mapRef - ref for the map instance in MapComponent
 * 
 * @returns {Function} handleMarkerClick
 * @returns {ClickedPoint[]} clickedPoints - from usePoiService
 * @returns {Line} currentLine - green line on the map
 * @returns {Object} draggableMarkerPosition
 * @returns {Function} handleMarkerDrag - function that handles the marker drag event
 * @returns {Function} handleMapClick - function that handles map click even
 * @returns {Function} setSelectedPoiJob - setter function that sets the currently selected job, which can be used for api calls
 * @returns {PoiGroupOption} selectedPoiGroup - currently selected poi group, which can be used for api calls, looks like: {value: 1, label: "POI_1"}
 * @returns {Function} setSelectedPoiGroup - setter function that sets the currently selected poi group, which can be used for api calls
 * @returns {PoiGroupOption[]} poiGroups - array of poi groups to fill dropdown in MapComponent, with label and value keys
 * 
 * @category Hooks - POI Map
 */

function usePoiMap({ mapRef, openPoiOnMap, polyLineArr }) {

    /********************************************/
    // local state for usePoiMap
    /********************************************/

    const [currentClickedPoint, setCurrentClickedPoint] = useState(null);

    const [currentLine, setCurrentLine] = useState({});
    const [iwData, setIwData] = useState(null); //Info window state

    const [selectedPoiJob, setSelectedPoiJob] = useState(null);
    const [selectedPoiGroup, setSelectedPoiGroup] = useState(null);
    const [selectedPoiRun, setSelectedPoiRun] = useState(null);
    const [activeComponent, setActiveComponent] = useState("PoiDialog");

    /********************************************/
    //bring in variables and functions from specialized poi hooks
    /********************************************/

    const infoWindow = useInfoWindow(mapRef);
    const poiService = usePoiService();
    const poiGroupService = usePoiGroupService();
    const polylineManager = usePolylineManager(mapRef);
    const markerManager = useMarkerManager({
        state: {currentClickedPoint, currentLine, lines: poiService.lines, clickedPoints: poiService.clickedPoints, selectedPoiGroup},
        setters: {setCurrentClickedPoint, setCurrentLine, setIwData, setLines: poiService.setLines, setClickedPoints: poiService.setClickedPoints, setActiveComponent},
        polylineManager,
        resetLines: poiService.resetLines,
        removeUnsavedClickedPoint: poiService.removeUnsavedClickedPoint
    });

    const poiDialogHandlers = usePoiDialogHandlers({
        state: {iwData, currentLine, currentClickedPoint, selectedPoiGroup, selectedPoiRun, polyLineArr},
        setters: {setIwData, setCurrentLine, setCurrentClickedPoint, setActiveComponent, setSelectedPoiGroup},
        hooks: {poiService, poiGroupService, polylineManager, markerManager, infoWindow}
    });

    const page = window.location.pathname
    const {hasUnsavedChanges} = useUnsavedChanges()

    const userContext = useUser()
    /********************************************/
    //useEffects
    /********************************************/

    //when page loads, check if map was open from table to show a specific poi
    useEffect(() => {
        if(openPoiOnMap && polyLineArr.length > 0){
            //fetch single poi
            const fetchPOI = async() => {
                const {point, lines} = await poiService.fetchSinglePOI(openPoiOnMap.poiId, openPoiOnMap.jobId, openPoiOnMap.poiGroupId)
            
                markerManager.handleOpenPoiFromTable(point, polyLineArr, lines)


            }
            fetchPOI()
        }
    }, [polyLineArr])  

    /*when selected job changes, fetch poi groups */
    useEffect(() => {
        if(!selectedPoiJob) return;

        if(page === "/poi-edit"){
            poiGroupService.fetchPOIGroupsByJob(selectedPoiJob);
        } else {
            poiGroupService.fetchPOIGroupsByUser(selectedPoiJob);
        }

    }, [selectedPoiJob]); //eslint-disable-line

    /*when selected group changes, reset map, check that group isn't null, then call API to fetch POI data */
    useEffect(() => {
        //reset infowindow, markers and lines
        infoWindow.closeInfoWindow()
        poiService.resetLines(polylineManager)
        setCurrentLine({})
        setCurrentClickedPoint(null)
        markerManager.resetMarkerState()

        if(!selectedPoiGroup) return;
        if(selectedPoiGroup.label.includes("All POIs")){
            poiService.fetchPOIs(selectedPoiJob);

        } else{
            poiService.fetchPOIs(selectedPoiJob, selectedPoiGroup)
        }
    }, [selectedPoiGroup]); //eslint-disable-line

    /*Open the Infowindow*/
    useEffect(() => {
        const iwComponent = <MapDialog
            data={iwData}
            poiDialogHandlers={poiDialogHandlers}
            selectedPoiJob={selectedPoiJob}
            selectedPoiGroup={selectedPoiGroup}
            activeComponent={activeComponent}
        />

        infoWindow.openInfoWindow(iwComponent, currentClickedPoint);
    }, [currentClickedPoint]); //eslint-disable-line

    /*Update the infowindow content (iwData)*/

    /* because exporting functions from our other hooks can hold onto stale state, we need to refresh the info window when hasUnsavedChanges updates, as well as the poi dialog's active component */
    /* for example: if a new POI was added, and the Add To My Groups button was clicked, the POI was saved, because we need a POIID from the database to save the POI to groups. The save function updated hasUnsavedChanges to false, but the handleDialogCancel function was holding onto the previous state of true, which triggered the unsaved changes warning modal to show up, even though the POI was already saved */
    useEffect(() => {

        const iwComponent = <MapDialog
            data={iwData}
            poiDialogHandlers={poiDialogHandlers}
            selectedPoiJob={selectedPoiJob}
            selectedPoiGroup={selectedPoiGroup}
            activeComponent={activeComponent}
            userContext={userContext}
        />

        infoWindow.updateInfoWindowContent(iwComponent);
    }, [iwData, hasUnsavedChanges, activeComponent]); //eslint-disable-line

    /* reset map when 'lines' state in usePoiService changes*/
    useEffect(() => {
        polylineManager.clearPolylines()
        polylineManager.addPolylines(poiService.lines);
    }, [poiService.lines]) //eslint-disable-line


    /********************************************/
    //return values from usePoiMap and other poi hooks
    /********************************************/

    return {
        handleMarkerClick: markerManager.handleMarkerClick,
        clickedPoints: poiService.clickedPoints,
        currentLine,
        draggableMarkerPosition: markerManager.draggableMarkerPosition,
        handleMarkerDrag: markerManager.handleMarkerDrag,
        handleMapClick: markerManager.handleMapClick,
        setSelectedPoiJob,
        selectedPoiGroup,
        setSelectedPoiGroup,
        poiGroups: poiGroupService.poiGroupOptions, //for filling dropdown,
        permissionSortedPoiGroups: poiGroupService.permissionSortedPoiGroups,
        currentClickedPoint,
        setSelectedPoiRun,
        handleCreatePointFromCustomIntersect: markerManager.handleCreatePointFromCustomIntersect,
        handleDialogCancel: poiDialogHandlers.handleDialogCancel
    }
}

export default usePoiMap;